/* lx1-accuracy-modal.js — behaviour for the "how accurate is Moasure LX1?" sheet (sections/lx1-accuracy-modal.liquid).
   Opens the <dialog> from any a[href="#lx1-accuracy"] or [data-lx1acc-open]; chips pick what is measured (room width /
   single wall / whole plan); the slider sets the size and the tag on the plan, the big answer and the note follow.
   Maths (per the 23 Oct 2026 accuracy statement): room width = the band figure typed in the section settings up to
   band 2, then ± band3_pct% of the length;
   single wall = ± wall_pct% of length + wall_fixed (in or cm); whole plan = ± plan_pct% of overall length.
   Units come from data-units: imperial (ft/in), metric (m/cm) or paired (metric, imperial in brackets).
   The size line under the plan ("About the length of a school bus") uses assets/mo-size-compare.js, loaded first by
   the section, so it is worded exactly like the accuracy page's calculators.
   Loaded deferred by the section. */
(function () {
  'use strict';

  // [min, max, start] per chip. The same realistic ranges as the accuracy page's LX1 calculator (27 Oct 2026);
  // metric is the same sizes rounded to whole metres.
  var RANGES = {
    imperial: { room: [5, 80, 24], wall: [3, 40, 12], plan: [10, 150, 50] },
    metric: { room: [2, 25, 7], wall: [1, 12, 4], plan: [3, 45, 15] }
  };
  var FRACTIONS = { 0: '', 0.25: '¼', 0.5: '½', 0.75: '¾' };

  function inches(x) { // ± x inches → nearest quarter, with ft once it gets long
    var q = Math.round(x * 4) / 4;
    if (q >= 24) { var ft = Math.floor(q / 12), rest = q - ft * 12; return ft + ' ft' + (rest ? ' ' + inches(rest) : ''); }
    var whole = Math.floor(q), frac = FRACTIONS[q - whole] || '';
    var num = whole ? whole + frac : (frac || '0');
    return num + ' in';
  }
  function cm(x) { // ± x cm → whole cm (one decimal under 2 cm), m once it gets long
    if (x >= 100) return (Math.round(x / 10) / 10).toString().replace('.', '.') + ' m';
    if (x < 2) return (Math.round(x * 10) / 10) + ' cm';
    return Math.round(x) + ' cm';
  }
  function lengthLabel(units, v) {
    if (units === 'imperial') return v + ' ft';
    if (units === 'paired') return v + ' m (' + Math.round(v * 3.28084) + ' ft)';
    return v + ' m';
  }
  function tolLabel(units, x) { // x in the store's small unit (in or cm)
    if (units === 'imperial') return '± ' + inches(x);
    if (units === 'paired') return '± ' + cm(x) + ' (' + inches(x / 2.54) + ')';
    return '± ' + cm(x);
  }

  function setup(dialog) {
    var units = dialog.getAttribute('data-units') || 'imperial';
    var sys = units === 'imperial' ? 'imperial' : 'metric';
    var bigUnit = sys === 'imperial' ? 'ft' : 'm';
    var band1 = parseFloat(dialog.getAttribute('data-band1-max')) || 30;
    var band2 = parseFloat(dialog.getAttribute('data-band2-max')) || 60;
    var band3Pct = parseFloat(dialog.getAttribute('data-band3-pct')) || 0.5;
    var wallPct = parseFloat(dialog.getAttribute('data-wall-pct')) || 1;
    var wallFixed = parseFloat(dialog.getAttribute('data-wall-fixed')) || 0;
    var planPct = parseFloat(dialog.getAttribute('data-plan-pct')) || 0.75;
    var toSmall = sys === 'imperial' ? 12 : 100; // ft→in, m→cm

    var q = function (sel) { return dialog.querySelector(sel); };
    var card = q('[data-lx1acc-card]');
    var range = q('[data-lx1acc-range]');
    var value = q('[data-lx1acc-value]');
    var sliderLabel = q('[data-lx1acc-slider-label]');
    var lead = q('[data-lx1acc-lead]');
    var big = q('[data-lx1acc-big]');
    var note = q('[data-lx1acc-note]');
    var bands = q('[data-lx1acc-bands]');
    var scale = q('[data-lx1acc-scale]');
    var marks = dialog.querySelectorAll('[data-lx1acc-mark]');
    var tags = {};
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-lx1acc-tag]'), function (t) { tags[t.getAttribute('data-lx1acc-tag')] = t; });
    var layers = dialog.querySelectorAll('[data-lx1acc-layer]');
    var chips = dialog.querySelectorAll('[data-lx1acc-state]');
    var state = 'room';
    var refEl = q('[data-lx1acc-ref]'), sizes = window.moSizeCompare, refWords = null, refLists = {};
    if (refEl && sizes) {
      var w = (refEl.getAttribute('data-words') || '').split('|');
      refWords = { about: w[0], over: w[1], under: w[2], half: w[3], count: w[4], times: w[5] };
      ['room', 'wall', 'plan'].forEach(function (k) { refLists[k] = sizes.parse(refEl.getAttribute('data-refs-' + k)); });
    }

    function tolerance(v) { // returns {text, small} for the current state and slider value v (ft or m)
      var small = v * toSmall;
      if (state === 'room') { // bands 1 and 2 are the typed figures; beyond band 2 it is a percentage, worked out like the others
        if (v <= band1) return bands.getAttribute('data-band1');
        if (v <= band2) return bands.getAttribute('data-band2');
        return tolLabel(units, small * band3Pct / 100);
      }
      if (state === 'wall') return tolLabel(units, small * wallPct / 100 + wallFixed);
      return tolLabel(units, small * planPct / 100);
    }

    function render() {
      var v = parseFloat(range.value);
      var size = lengthLabel(units, v);
      var tol = tolerance(v);
      value.textContent = size;
      range.style.setProperty('--lx1acc-fill', ((v - range.min) / (range.max - range.min) * 100) + '%');
      lead.textContent = (lead.getAttribute('data-lead-' + state) || '').replace('[size]', size);
      big.textContent = tol;
      note.textContent = note.getAttribute('data-note-' + state) || '';
      var tagText = size + '  →  ' + tol;
      if (tags.room) tags.room.textContent = tagText;
      if (tags.wall) tags.wall.textContent = tagText;
      if (tags.plan) tags.plan.textContent = tagText;
      if (tags['plan-l']) tags['plan-l'].textContent = tol; // second axis: same tolerance, no size (the slider sets one dimension)
      if (refEl) { var line = refWords ? sizes.line(refWords, refLists[state], v) : ''; refEl.textContent = line; refEl.hidden = !line; }
    }

    function setState(next) {
      state = next;
      card.setAttribute('data-state', state);
      var r = RANGES[sys][state];
      range.min = r[0]; range.max = r[1]; range.value = r[2];
      sliderLabel.textContent = sliderLabel.getAttribute('data-label-' + state) || '';
      // band markers only make sense for room widths
      var showMarks = state === 'room';
      Array.prototype.forEach.call(marks, function (m, i) {
        var at = i === 0 ? band1 : band2;
        m.hidden = !showMarks || at <= r[0] || at >= r[1];
        m.style.left = ((at - r[0]) / (r[1] - r[0]) * 100) + '%';
      });
      scale.innerHTML = '';
      var steps = showMarks ? [r[0], band1, band2, r[1]] : [r[0], Math.round((r[0] + r[1]) / 2), r[1]];
      steps.forEach(function (s) { var span = document.createElement('span'); span.textContent = s + ' ' + bigUnit; scale.appendChild(span); });
      Array.prototype.forEach.call(layers, function (l) { // SVG elements have no .hidden property, so toggle the attribute
        if (l.getAttribute('data-lx1acc-layer') === state) l.removeAttribute('hidden'); else l.setAttribute('hidden', '');
      });
      Object.keys(tags).forEach(function (k) { tags[k].hidden = !(k === state || (state === 'plan' && k === 'plan-l')); });
      Array.prototype.forEach.call(chips, function (c) {
        var on = c.getAttribute('data-lx1acc-state') === state;
        c.classList.toggle('is-on', on); c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      render();
    }

    Array.prototype.forEach.call(chips, function (c) { c.addEventListener('click', function () { setState(c.getAttribute('data-lx1acc-state')); }); });
    range.addEventListener('input', render);

    // full table + questions
    var toggle = q('[data-lx1acc-toggle]');
    if (toggle) toggle.addEventListener('click', function () {
      var target = document.getElementById(toggle.getAttribute('data-lx1acc-toggle'));
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (target) target.hidden = open;
    });
    var qs = dialog.querySelectorAll('[data-lx1acc-q]');
    Array.prototype.forEach.call(qs, function (btn) {
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        Array.prototype.forEach.call(qs, function (b) { // one open at a time, like the tab band's accordions
          b.setAttribute('aria-expanded', 'false');
          var a = document.getElementById(b.getAttribute('aria-controls')); if (a) a.hidden = true;
        });
        if (!open) { btn.setAttribute('aria-expanded', 'true'); var ans = document.getElementById(btn.getAttribute('aria-controls')); if (ans) ans.hidden = false; }
      });
    });

    // open / close
    function open(source) {
      setState('room');
      if (typeof dialog.showModal === 'function') { if (!dialog.open) dialog.showModal(); }
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('lx1acc-open');
      var sheetEl = dialog.querySelector('.lx1acc__sheet');
      sheetEl.scrollTop = 0;
      try { sheetEl.focus({ preventScroll: true }); } catch (err) { sheetEl.focus(); } // the sheet, not the close button: no focus ring on open, Tab goes to the chips next
      window.requestAnimationFrame(function () { sheetEl.scrollTop = 0; }); // the browser's own focus/hash scroll lands after showModal
      if (window.dataLayer) window.dataLayer.push({ event: 'lx1_accuracy_modal', action: 'open', source: source || 'link' });
    }
    function close() {
      dialog.style.transform = '';
      if (dialog.open && typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
    }
    dialog.addEventListener('close', function () { document.documentElement.classList.remove('lx1acc-open'); });
    var sheet = dialog.querySelector('.lx1acc__sheet'), head = dialog.querySelector('.lx1acc__head');
    if (sheet && head) sheet.addEventListener('scroll', function () { head.classList.toggle('is-stuck', sheet.scrollTop > 4); }, { passive: true });
    // Bottom sheet: drag the header (handle) down to close. Follows the finger, snaps back under 90px, closes beyond.
    if (head && window.PointerEvent) {
      var dragY = null, dragDy = 0;
      head.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' || e.target.closest('[data-lx1acc-close]')) return;
        dragY = e.clientY; dragDy = 0; dialog.classList.add('is-dragging'); head.setPointerCapture(e.pointerId);
      });
      head.addEventListener('pointermove', function (e) {
        if (dragY === null) return;
        dragDy = Math.max(0, e.clientY - dragY);
        dialog.style.transform = 'translateY(' + dragDy + 'px)';
      });
      var endDrag = function () {
        if (dragY === null) return;
        dragY = null; dialog.classList.remove('is-dragging');
        if (dragDy > 90) { dialog.style.transform = ''; close(); return; }
        dialog.classList.add('is-settling'); dialog.style.transform = '';
        setTimeout(function () { dialog.classList.remove('is-settling'); }, 240);
      };
      head.addEventListener('pointerup', endDrag); head.addEventListener('pointercancel', endDrag);
    }
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-lx1acc-close]'), function (b) { b.addEventListener('click', close); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); }); // backdrop tap
    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('a[href="#lx1-accuracy"], a[href$="#lx1-accuracy"], [data-lx1acc-open]') : null;
      if (!t) return;
      e.preventDefault();
      var host = t.closest('[data-section-id]');
      open(t.getAttribute('data-lx1acc-open') || (host ? host.getAttribute('data-section-id') : 'link'));
    });
    if (location.hash === '#lx1-accuracy') open('hash');
    setState('room');
  }

  function init() { Array.prototype.forEach.call(document.querySelectorAll('[data-lx1acc]'), function (d) { if (!d.__lx1acc) { d.__lx1acc = true; setup(d); } }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
